import { useRef, useState } from "react"
import { useSession } from "../../auth/session"
import { useDispatch, useSelector } from "react-redux"
import { logIn, meSelector } from "../../redux/globalSlice"
import { updateProfileThunk } from "../../redux/usersSlice"
import { AppDispatch } from "../../redux/store"
import MfaEnrollModal, { MfaEnrollModalHandle } from "../MfaEnrollModal"
import ChangePasswordDialog from "../ChangePasswordDialog"
import ChangeEmailDialog from "../ChangeEmailDialog"
import Api from "../../api/api"
import AvatarUploadDialog from "../AvatarUploadDialog"
import Avatar from "../widgets/Avatar"
import Spinner from "../widgets/Spinner"
import NotificationPreferencesCard from "../NotificationPreferencesCard"
import IlokAccountsCard from "../IlokAccountsCard"
import { MFA_REQUIRED } from "../../auth/mfaPolicy"

export default function AccountPage() {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const dispatch = useDispatch<AppDispatch>()
  // undefined until the /me bootstrap completes — happens on a hard reload
  // of this page, so no non-null assertion here
  const me = useSelector(meSelector)?.data
  const mfaModalRef = useRef<MfaEnrollModalHandle>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [showEmailDialog, setShowEmailDialog] = useState(false)

  // /me reads the email live from Cognito, so a confirmed change shows up
  // on a plain refetch
  const closeEmailDialog = (changed: boolean) => {
    setShowEmailDialog(false)
    if (changed && accessToken)
      Api.getMe(accessToken).then(me => dispatch(logIn(me))).catch(err => console.error("Could not refresh account data:", err))
  }

  // name edit mode
  const [editingName, setEditingName] = useState(false)
  const [idCopied, setIdCopied] = useState(false)
  const [firstname, setFirstname] = useState("")
  const [lastname, setLastname] = useState("")
  const [savingName, setSavingName] = useState(false)
  const [nameError, setNameError] = useState("")

  const startNameEdit = () => {
    setFirstname(me?.firstname ?? "")
    setLastname(me?.lastname ?? "")
    setNameError("")
    setEditingName(true)
  }

  const saveName = async () => {
    if (!me) return
    setSavingName(true)
    setNameError("")
    try {
      await dispatch(updateProfileThunk({
        id: me.id,
        updates: { firstname: firstname.trim(), lastname: lastname.trim() },
        accessToken,
      })).unwrap()
      setEditingName(false)
    } catch (err) {
      // the optimistic update already rolled back in redux
      setNameError(err instanceof Error ? err.message : "unknown error")
    } finally {
      setSavingName(false)
    }
  }

  return (
    <div className="page">
      <h1 className="page-title">Account Settings</h1>

      <div className="card" style={{ maxWidth: 480 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Avatar imageUrl={me?.imageUrl} size={64} />
          <button
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            onClick={() => fileInputRef.current?.click()}
          >
            Change photo
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) setAvatarFile(file)
              // allow re-picking the same file after cancel
              e.target.value = ""
            }}
          />
        </div>

        {!editingName ? (
          <>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">First Name</span>
                <span className="detail-value">{me?.firstname ?? ""}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Last Name</span>
                <span className="detail-value">{me?.lastname ?? ""}</span>
              </div>
              <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
                <span className="detail-label">Email</span>
                <span className="detail-value">{me?.email ?? ""}</span>
              </div>
              {/* the id other customers use to send licenses to this account
                  (Licenses → Transfer); the username chosen at sign-up */}
              <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
                <span className="detail-label">Account ID</span>
                <span className="detail-value" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontFamily: "var(--al-mono-font-family)" }}>{me?.id ?? ""}</span>
                  {me?.id && navigator.clipboard && (
                    <button
                      type="button"
                      className="card-link"
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "9pt" }}
                      onClick={() => { navigator.clipboard.writeText(me.id).then(() => setIdCopied(true), () => {}) }}
                    >
                      {idCopied ? "Copied" : "Copy"}
                    </button>
                  )}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Terms of Use</span>
                <span className="detail-value">{me?.policyVersions?.terms ? `Accepted version ${me.policyVersions.terms}` : "Not recorded"}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Privacy Policy</span>
                <span className="detail-value">{me?.policyVersions?.privacy ? `Accepted version ${me.policyVersions.privacy}` : "Not recorded"}</span>
              </div>
            </div>

            <div className="subscription-actions">
              <button onClick={startNameEdit}>Edit</button>
              <button onClick={() => setShowPasswordDialog(true)}>
                Change Password
              </button>
              <button onClick={() => setShowEmailDialog(true)}>
                Change Email
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">First Name</span>
                <input
                  type="text"
                  autoComplete="given-name"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="generate-codes-input"
                  disabled={savingName}
                  autoFocus
                />
              </label>
              <label className="generate-codes-field">
                <span className="generate-codes-label">Last Name</span>
                <input
                  type="text"
                  autoComplete="family-name"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className="generate-codes-input"
                  disabled={savingName}
                />
              </label>
            </div>

            {nameError && <p className="generate-codes-error">{nameError}</p>}

            <div className="subscription-actions">
              <button onClick={() => setEditingName(false)} disabled={savingName}>Cancel</button>
              <button onClick={saveName} disabled={savingName}>
                {savingName ? <><Spinner size={12} /> Saving…</> : "Save"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Hidden when the environment doesn't use MFA (the pool may not even
          have MFA enabled there, so enrollment would fail). */}
      {MFA_REQUIRED && (
        <div className="card" style={{ maxWidth: 480, marginTop: 16 }}>
          <div className="detail-grid">
            <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
              <span className="detail-label">Two-Factor Authentication</span>
              <span className="detail-value">
                {me?.mfaEnabled ? "Enabled" : "Not enabled"}
              </span>
            </div>
          </div>

          {!me?.mfaEnabled && (
            <div className="subscription-actions">
              <button onClick={() => mfaModalRef.current?.open()}>
                Enable Two-Factor Authentication
              </button>
            </div>
          )}
        </div>
      )}

      <NotificationPreferencesCard accessToken={accessToken} />

      <IlokAccountsCard accessToken={accessToken} />

      <MfaEnrollModal ref={mfaModalRef} />

      {showPasswordDialog && (
        <ChangePasswordDialog onClose={() => setShowPasswordDialog(false)} />
      )}

      {showEmailDialog && (
        <ChangeEmailDialog currentEmail={me?.email ?? ""} onClose={closeEmailDialog} />
      )}

      {avatarFile && (
        <AvatarUploadDialog file={avatarFile} onClose={() => setAvatarFile(null)} />
      )}
    </div>
  )
}
